// ============================================================================
// Gmail Adapter — public surface + registration helper.
//
// This file is the one the rest of the module imports. At module-load time,
// `registerGmailAdapter()` wires the implementation into BOTH registries:
//
//   1. `webhook-adapters` (slug-keyed) — powers the public ingress route
//      `POST /api/v1/connectors/webhooks/gmail/:instanceId`. The webhook
//      itself receives a Google Cloud Pub/Sub push (NOT a direct Gmail POST)
//      authenticated via a per-instance bearer token; the adapter parses
//      the envelope into a `email.history.changed` event carrying the
//      historyId, and the dispatcher resolves changed message ids via
//      `users.history.list`.
//   2. `capability-registry` (typeId-keyed) — lets any cross-module caller
//      reach Gmail via `call(instanceId, 'email', 'sendEmail', ...)`,
//      `listInbox`, `getEmail`, `getAttachment`, `markAsRead`,
//      `subscribeInbox`, `unsubscribeInbox`.
//
// Matching up the keys: the `006_gmail_seed.ts` migration sets
// `connectors_types.id = connectors_types.slug = 'gmail'`, so the same
// string addresses the row from every angle (instance.type_id, URL path,
// capability register, webhook register).
// ============================================================================

import { register as registerCapability } from '../../services/capability-registry'
import { registerWebhookAdapter } from '../../services/webhook-adapters'
import { buildGmailMethods } from './capability-methods'
import { gmailWebhookAdapter } from './webhook'

/** Canonical type id / slug. MUST match the `connectors_types` seed row. */
export const GMAIL_TYPE_SLUG = 'gmail'

export interface RegisterGmailOpts {
  /** Injectable fetch — tests swap it out. Defaults to the global `fetch`. */
  fetchImpl?: typeof fetch
}

/**
 * Register the Gmail adapter in both the webhook and capability registries.
 * Idempotent: re-calling just overwrites the prior registration (matches the
 * semantics of the underlying registries). Safe to call at module-load +
 * test-reset points without special-casing.
 */
export function registerGmailAdapter(opts: RegisterGmailOpts = {}): void {
  registerWebhookAdapter(GMAIL_TYPE_SLUG, gmailWebhookAdapter)
  registerCapability(
    GMAIL_TYPE_SLUG,
    'email',
    buildGmailMethods(opts),
  )
}

// ============================================================================
// Barrel — re-export the pieces consumers might reach for directly.
// ============================================================================

export {
  gmailWebhookAdapter,
  verifyGmailWebhookToken,
  parseGmailPubSubEnvelope,
} from './webhook'

export {
  buildGmailMethods,
  type GmailMethodsDeps,
} from './capability-methods'

export {
  GMAIL_AUTHORIZE_URL,
  GMAIL_TOKEN_URL,
  GMAIL_API_BASE,
  GMAIL_DEFAULT_SCOPES,
  buildGmailAuthorizeUrl,
  exchangeGmailCode,
  refreshGmailToken,
  redactGmailSecrets,
  GmailOAuthError,
  type GmailAuthorizeInput,
  type GmailAuthorizeResult,
  type ExchangeGmailCodeInput,
  type ExchangeGmailCodeResult,
  type RefreshGmailTokenInput,
  type GmailOAuthErrorCode,
} from './oauth'

export {
  buildMimeMessage,
  base64UrlEncodeMessage,
  base64UrlDecodeToString,
  base64UrlDecodeToBytes,
  parseMimeMessage,
  parseAddressList,
  decodeHeaderValue,
  encodeQuotedPrintable,
  decodeQuotedPrintable,
} from './mime'

export {
  sendGmailMessage,
  GMAIL_MAX_RAW_SIZE,
  type SendGmailMessageInput,
  type SendGmailMessageOutput,
} from './send'

export {
  listGmailMessages,
  type ListGmailMessagesInput,
  type ListGmailMessagesOutput,
} from './list'

export {
  getGmailMessage,
  markGmailAsRead,
  type GetGmailMessageInput,
  type MarkGmailAsReadInput,
} from './get'

export {
  getGmailAttachment,
  type GetGmailAttachmentInput,
} from './attachments'

export {
  subscribeGmailInbox,
  unsubscribeGmailInbox,
  type SubscribeGmailInboxInput,
  type SubscribeGmailInboxOutput,
  type UnsubscribeGmailInboxInput,
} from './watch'

export {
  GmailError,
  GmailSignatureError,
  classifyGmailError,
  sanitizeGmailErrorMessage,
  parseRetryAfterMs as parseGmailRetryAfterMs,
  type GmailErrorCode,
  type GmailErrorDetails,
  type GmailSendResponse,
  type GmailMessagesListResponse,
  type GmailMessage,
  type GmailMessagePart,
  type GmailAttachmentPayload,
  type GmailWatchResponse,
  type GmailHeader,
  // NB: GoogleErrorEnvelope / GoogleError / GoogleErrorDetail are shared with
  // the P8 google-calendar adapter's re-export surface. They're intentionally
  // NOT re-exported here to avoid ambiguous-module errors at `public-api.ts`.
} from './types'
